import React, { useEffect, useMemo, useState } from "react";
import { Autocomplete, Checkbox, Chip, TextField } from "@mui/material";

// An empty selection means all, including options that load later.
export default function OverviewMultiSelect({ inputId, options, value, onChange, allLabel, placeholder, onOpenChange, onSearchChange, loading, searchError, remoteSearch = false, maxSelections = 20 }) {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [known, setKnown] = useState({});
  useEffect(() => { setKnown(current => ({ ...current, ...Object.fromEntries(options.map(option => [option.value, option])) })); }, [options]);
  const byId = { ...known, ...Object.fromEntries(options.map(option => [option.value, option])) };
  const all = { value: "all", label: allLabel };
  const selectedKey = JSON.stringify(value.length ? value.map(id => byId[id]).filter(Boolean) : [all]);
  const selectedOptions = useMemo(() => JSON.parse(selectedKey), [selectedKey]);
  return <Autocomplete id={inputId} multiple disableCloseOnSelect openOnFocus size="small"
    inputValue={input} loading={loading} onInputChange={(_, text) => { setInput(text); onSearchChange?.(text); }} options={[all, ...options]} value={selectedOptions}
    {...(remoteSearch ? { filterOptions: items => items } : {})}
    getOptionLabel={(option) => option.label}
    getOptionDisabled={option => option.value !== "all" && !value.includes(option.value) && value.length >= maxSelections}
    isOptionEqualToValue={(option, selected) => option.value === selected.value}
    onChange={(_event, selected, reason, details) => {
      if (details?.option.value === "all" || reason === "clear") onChange([]);
      else onChange(selected.filter((option) => option.value !== "all").map((option) => option.value));
    }}
    disableClearable={!value.length} onOpen={() => { setOpen(true); onOpenChange?.(true); }} onClose={() => { setOpen(false); setInput(""); onSearchChange?.(""); onOpenChange?.(false); }}
    noOptionsText={searchError ? "Could not search practitioners. Try again." : "No matches"} clearText="Clear selections" openText="Show options" closeText="Close options"
    renderTags={(selected, getTagProps) => open ? null : selected[0]?.value === "all"
      ? <span style={{ margin: "2px 6px", color: "#405762" }}>{allLabel}</span>
      : <>{selected.slice(0, 2).map((option, index) => {
        const { key, ...tagProps } = getTagProps({ index });
        return <Chip key={key} {...tagProps} label={option.label} size="small" />;
      })}{selected.length > 2 && <span style={{ marginLeft: 6, color: "#526976" }}>+{selected.length - 2}</span>}</>}
    renderOption={(props, option, { selected }) => {
      const { key, ...optionProps } = props;
      return <li key={key} {...optionProps}>
        <Checkbox checked={selected} tabIndex={-1} disableRipple size="small"
          inputProps={{ "aria-hidden": true, tabIndex: -1 }}
          sx={{ pointerEvents: "none", p: 0, mr: 1.25, color: "#a3b3bc", "&.Mui-checked": { color: "#087c91" } }} />
        <span>{option.label}</span>
      </li>;
    }}
    renderInput={(params) => <TextField {...params} placeholder={open ? placeholder : ""} />}
    sx={{
      fontFamily: "inherit", fontSize: "inherit",
      "& .MuiOutlinedInput-root": { minHeight: 42, borderRadius: "8px", backgroundColor: "#fff", fontFamily: "inherit", fontSize: "inherit", gap: "2px" },
      "& .MuiOutlinedInput-notchedOutline": { borderColor: "#d5e0e5" },
      "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#9cb4c0" },
      "& .MuiOutlinedInput-root.Mui-focused": { boxShadow: "0 0 0 3px rgba(8,124,145,.10)" },
      "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#087c91", borderWidth: 1 },
      "& .MuiChip-root": { borderRadius: "5px", backgroundColor: "#edf4f6", color: "#365361", fontFamily: "inherit", maxWidth: "180px" },
      "& .MuiChip-deleteIcon": { color: "#7b929d", fontSize: 16 },
      "& .MuiAutocomplete-input::placeholder": { color: "#718894", opacity: 1 },
      "& .MuiAutocomplete-popupIndicator, & .MuiAutocomplete-clearIndicator": { color: "#718894" },
    }}
    componentsProps={{ paper: { sx: {
      mt: .75, border: "1px solid #e0e8ec", borderRadius: "10px", boxShadow: "0 8px 28px rgba(33,60,75,.14)",
      fontFamily: "inherit", fontSize: "var(--schedule-text-small, 14px)", color: "#365361",
      "& .MuiAutocomplete-listbox": { p: .75, maxHeight: 300 },
      "& .MuiAutocomplete-option": { minHeight: "40px !important", borderRadius: "6px", px: 1.25, my: .25 },
      "& .MuiAutocomplete-option[aria-selected='true']": { backgroundColor: "#edf6f7" },
      "& .MuiAutocomplete-option.Mui-focused, & .MuiAutocomplete-option[aria-selected='true'].Mui-focused": { backgroundColor: "#e3eff2" },
    } } }} />;
}
